import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Mail, Inbox, Search, Send, Play } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const CustomerSupportEmailResponderAgent = () => {
  const navigate = useNavigate();
  const [aboutRef, aboutVisible] = useScrollAnimation({ threshold: 0.1 });
  const [stepsRef, stepsVisible] = useScrollAnimation({ threshold: 0.1 });
  const [videoRef, videoVisible] = useScrollAnimation({ threshold: 0.1 });

  const handleBookDemo = () => {
    window.open('https://calendly.com/speakmulti/introduction', '_blank');
  };

  const handleBackToLibrary = () => {
    navigate('/agent-library/customer-service');
  };

  const steps = [
    {
      number: '01',
      title: 'Agent Activation & Email Triage',
      description: 'Auto-trigger on new inbox mail, use an LLM to classify (customer query vs promo/spam/irrelevant), and extract key questions into structured JSON. Non-relevant items are marked "Not relevant" and skipped.',
      icon: <Inbox className="w-6 h-6" />,
      color: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      number: '02',
      title: 'Query Analysis & Retrieval',
      description: 'Loop through each extracted query, search the knowledge base, and decide per-item whether it is answerable. Store results into "Answered" or "Unanswered" buckets to ensure full coverage.',
      icon: <Search className="w-6 h-6" />,
      color: 'from-purple-500/20 to-pink-500/20'
    },
    {
      number: '03',
      title: 'Draft, Dispatch & Escalate',
      description: 'Generate polite, personalized replies for fully answerable emails and send via the connected mail system. Create tickets for any unanswered queries and route them to agents for manual follow-up.',
      icon: <Send className="w-6 h-6" />,
      color: 'from-green-500/20 to-emerald-500/20'
    },
    {
      number: '04',
      title: 'Feedback & Continuous Improvement',
      description: 'Collect user/stakeholder feedback on accuracy, tone, and resolution quality; analyze patterns and tune prompts/rules so responses get faster, more precise, and more on-brand over time.',
      icon: <ArrowRight className="w-6 h-6" />,
      color: 'from-orange-500/20 to-amber-500/20'
    }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#12D7E8]/5 via-transparent to-transparent" />
        
        <div className="light-full-container relative z-10">
          <div className="light-content-container">
            <div className="max-w-5xl mx-auto">
              {/* Back Button */}
              <button 
                onClick={handleBackToLibrary}
                className="group flex items-center gap-2 text-[rgba(255,255,255,0.7)] hover:text-[#12D7E8] transition-colors mb-8"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span>Back to Customer Service Agent Library</span>
              </button>

              {/* Icon Badge */}
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 mb-6">
                <Mail className="w-8 h-8 text-blue-400" />
              </div>

              {/* Title */}
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-[#12D7E8] bg-clip-text text-transparent">
                Customer Support Email Responder Agent
              </h1>

              {/* Subtitle */}
              <p className="text-xl text-[rgba(255,255,255,0.8)] leading-relaxed mb-8 max-w-3xl">
                Monitors the email inbox for customer queries, retrieves answers from the knowledge base, sends replies, or creates tickets for unresolved queries.
              </p>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-sm text-[rgba(255,255,255,0.7)]">Production Ready</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]">
                  <span className="text-sm text-[rgba(255,255,255,0.7)]">KB-Powered</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)]">
                  <span className="text-sm text-[rgba(255,255,255,0.7)]">Auto-Escalation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section ref={videoRef} className="section-spacing relative">
        <div className="light-full-container">
          <div className="light-content-container">
            <div className="max-w-5xl mx-auto flex justify-center">
              <div className={`relative rounded-2xl overflow-hidden bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] w-[80%] ${videoVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                {/* Video container with aspect ratio */}
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <video 
                    className="absolute inset-0 w-full h-full object-cover"
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src="https://customer-assets.emergentagent.com/job_e3fcc9c5-d157-4bec-a159-d762260e1c37/artifacts/6oc3apy9_Customer%20Support%20Email%20Responder%20Agent.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                
                {/* Video caption */}
                <div className="p-4 bg-[rgba(0,0,0,0.3)] backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    <Play className="w-4 h-4 text-[#12D7E8]" />
                    <span className="text-sm text-[rgba(255,255,255,0.7)]">Watch: Building the Customer Support Email Responder Agent in OnePlace</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section ref={aboutRef} className="section-spacing relative">
        <div className="light-full-container">
          <div className="light-content-container">
            <div className="max-w-5xl mx-auto">
              <div className={`${aboutVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
                {/* About the Agent */}
                <div className="mb-16">
                  <h2 className="text-3xl font-bold mb-6 text-white">About the Agent</h2>
                  <p className="text-lg text-[rgba(255,255,255,0.7)] leading-relaxed">
                    OnePlace Customer Support Email Responder Agent automates the handling of customer emails, enhancing efficiency and accuracy in response generation. By leveraging a Large Language Model (LLM), it analyzes customer inquiries, extracts essential information from a dynamic knowledge base, and crafts precise, personalized responses.
                  </p>
                </div>

                {/* How It Works */}
                <div>
                  <h2 className="text-3xl font-bold mb-4 text-white">How The Agent Works</h2>
                  <p className="text-lg text-[rgba(255,255,255,0.7)] leading-relaxed mb-12">
                    The agent monitors your inbox, classifies incoming emails, retrieves knowledge base answers, generates personalized responses, and escalates unresolved queries - all with continuous learning and improvement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section ref={stepsRef} className="section-spacing relative">
        <div className="light-full-container">
          <div className="light-content-container">
            <div className="max-w-5xl mx-auto">
              <div className="space-y-6">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`step-card group ${stepsVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    {/* Gradient background on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${step.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl`} />
                    
                    <div className="relative z-10 flex gap-6">
                      {/* Step number */}
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-xl bg-[#12D7E8]/10 border border-[#12D7E8]/30 flex items-center justify-center group-hover:bg-[#12D7E8]/20 transition-colors">
                          <span className="text-2xl font-bold text-[#12D7E8]">{step.number}</span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="p-2 rounded-lg bg-[rgba(255,255,255,0.05)] text-[#12D7E8] group-hover:scale-110 transition-transform">
                            {step.icon}
                          </div>
                          <h3 className="text-xl font-bold text-white group-hover:text-[#12D7E8] transition-colors">
                            {step.title}
                          </h3>
                        </div>
                        <p className="text-[rgba(255,255,255,0.7)] leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#12D7E8]/5 to-transparent" />
        
        <div className="light-full-container relative z-10">
          <div className="light-content-container">
            <div className="max-w-4xl mx-auto text-center">
              <div className="relative bg-gradient-to-br from-[rgba(255,255,255,0.05)] to-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)] rounded-3xl p-12 md:p-16">
                {/* Decorative blur */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#12D7E8]/10 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <h2 className="text-4xl md:text-5xl font-bold mb-6">
                    Ready to Automate Your <span className="text-[#12D7E8]">Email Support?</span>
                  </h2>
                  <p className="text-xl text-[rgba(255,255,255,0.7)] mb-10 leading-relaxed">
                    See how the Customer Support Email Responder Agent can reduce response times, improve accuracy, and deliver consistent support at scale.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <button 
                      onClick={handleBookDemo}
                      className="group px-8 py-4 bg-[#12D7E8] text-black font-semibold rounded-lg hover:bg-[#33E1FF] transition-all duration-300 flex items-center gap-2 shadow-lg shadow-[#12D7E8]/20 hover:shadow-[#12D7E8]/40 hover:scale-105"
                    >
                      Book a Demo
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                    
                    <button 
                      onClick={handleBackToLibrary}
                      className="group px-8 py-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white font-semibold rounded-lg hover:bg-[rgba(255,255,255,0.1)] hover:border-[#12D7E8]/50 transition-all duration-300 flex items-center gap-2"
                    >
                      <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                      Back to Customer Service Agents
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }

        .step-card {
          position: relative;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.75rem;
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .step-card:hover {
          transform: translateX(8px);
          border-color: rgba(18, 215, 232, 0.3);
          box-shadow: 0 10px 40px rgba(18, 215, 232, 0.1);
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default CustomerSupportEmailResponderAgent;
